import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Window from '@lib/window-size';
import { useAgentData } from '@lib/hooks';

// ---------------------------------------------------------

import Button from '@components/button';
import Content from '@components/content';
import Icon from '@components/icon';
import Image from '@components/image';
import Link from '@components/link';
import Quote from '@components/quote';
import EnhancedContactForm from '@components/forms/enhanced';
import FormRealtorCards from '@components/forms/enhanced/realtor-cards';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  blog_detail,
  blog_detail_header,
  blog_detail_header_container,
  blog_detail_header_text,
  blog_detail_header_img,
  blog_detail_container,
  blog_detail_support,
  blog_detail_content,
  blog_detail_social,
  blog_detail_social_link,
  blog_detail_article,
  blog_detail_article_main,
  blog_detail_article_support,
  blog_detail_mobile_support,
  blog_detail_article_meta,
  blog_detail_article_categories,
  blog_detail_buttons
} = styles;

// ---------------------------------------------------------

import Components from '@layout/components';

// ---------------------------------------------------------

const BlogDetail = (props) => {
  const { article, components, header, associatedAgent: associatedAgentProp } = props;

  // ---------------------------------------------------------

  const {
    associatedAgent,
    setAssociatedAgent,
    setAssociatedAgentToSessionStorage,
    getAssociatedAgentFromSessionStorage
  } = useAgentData(associatedAgentProp);
  const router = useRouter();
  const classes = classNames(blog_detail);
  const mediaIsMedium = Window().width > 1040;

  // ---------------------------------------------------------

  useEffect(() => {
    // set agent data to session storage if it was provided by the server
    if (associatedAgentProp) {
      setAssociatedAgentToSessionStorage(associatedAgentProp);
    }
  }, [associatedAgentProp]);

  useEffect(() => {
    // set associated agent data if it exists in session storage
    // for a case where the user comes from a page with an agentid in the query
    if (!associatedAgentProp) {
      const agentData = getAssociatedAgentFromSessionStorage();
      if (agentData) {
        setAssociatedAgent(agentData);
      }
    }
  }, [router, associatedAgentProp]);

  const SocialLinks = (item) => {
    return (
      <Link
        key={item?.aria}
        className={blog_detail_social_link}
        url={item?.url}
        ariaLabel={item?.aria}
      >
        <Icon type={'social'} name={item?.icon} />
      </Link>
    );
  };

  return (
    <div className={classes}>
      <header className={blog_detail_header}>
        <div className={blog_detail_header_container}>
          {!mediaIsMedium && (
            <div className={blog_detail_header_img}>
              <Image
                image={header?.image}
                backgroundImage
                objectFit={'cover'}
                layout={'fill'}
                priority
              />
            </div>
          )}
          <div className={blog_detail_header_text}>
            <h1>{header?.title}</h1>
            <p>{header?.subtitle}</p>
          </div>
        </div>
      </header>
      {mediaIsMedium && (
        <div className={blog_detail_header_img}>
          <Image
            image={header?.image}
            backgroundImage
            objectFit={'cover'}
            layout={'fill'}
            priority
          />
        </div>
      )}
      <main>
        <div className={blog_detail_container}>
          <div className={blog_detail_support}>
            {associatedAgent && (
              <EnhancedContactForm
                primaryAgent={associatedAgent}
                sticky={true}
                blogPath={components[0].slug}
                blogTitle={header.title}
                agentId={associatedAgent.agentId}
                endpoint="blog information request for agent card"
              >
                <FormRealtorCards primaryContact={associatedAgent} />
              </EnhancedContactForm>
            )}
          </div>

          <article className={blog_detail_content}>
            <div className={blog_detail_social}>
              {article?.socialList.map((item, key) => SocialLinks(item, key))}
            </div>

            <div className={blog_detail_article}>
              <div className={blog_detail_article_main}>
                {<Content content={props?.article?.details?.mainContent} />}
              </div>

              <div className={blog_detail_mobile_support}>
                {article?.quote?.body && <Quote {...article?.quote} />}
              </div>

              {associatedAgent && (
                <div className={blog_detail_mobile_support}>
                  <EnhancedContactForm
                    primaryAgent={associatedAgent}
                    sticky={false}
                    blogPath={components[0].slug}
                    blogTitle={header.title}
                    agentId={associatedAgent.agentId}
                    endpoint="blog information request for agent card"
                  >
                    <FormRealtorCards primaryContact={associatedAgent} />
                  </EnhancedContactForm>
                </div>
              )}

              <div className={blog_detail_article_support}>
                {<Content content={props?.article?.details?.supportContent} />}
              </div>
            </div>

            {article?.author && (
              <div className={blog_detail_article_meta}>
                <p>{article?.author}</p>
              </div>
            )}

            {article?.categories && (
              <div className={blog_detail_article_categories}>
                <span>Article Categories</span>
                <div className={blog_detail_buttons}>
                  {article?.categories?.map((item, key) => (
                    <Button
                      key={key}
                      theme={'gray-outline'}
                      label={item?.title}
                      url={`/blog/category/${item.slug}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>

        <div>
          {components.map((item, key) => (
            <Components key={key} {...item} />
          ))}
        </div>
      </main>
    </div>
  );
};

BlogDetail.propTypes = {
  /**
   * data to be used in the article detail
   */
  article: PropTypes.shape({
    author: PropTypes.string,
    callout: PropTypes.object,
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        blocksCollection: PropTypes.object,
        linkedFrom: PropTypes.object,
        name: PropTypes.string,
        slug: PropTypes.string,
        sys: PropTypes.object
      })
    ),
    details: PropTypes.shape({
      mainContent: PropTypes.object,
      supportContent: PropTypes.object
    }),
    quote: PropTypes.shape({
      body: PropTypes.string
    }),
    socialList: PropTypes.arrayOf(PropTypes.object)
  }),
  /**
   * components to be used throughout the page.
   */
  components: PropTypes.arrayOf(PropTypes.object),
  /**
   * data to be used in the header component
   */
  header: PropTypes.shape({
    image: PropTypes.object,
    subtitle: PropTypes.string,
    title: PropTypes.string
  }),

  /**
   * data to be used in the EnhancedContactForm and FormRealtorCards components
   */
  associatedAgent: PropTypes.shape({
    agentId: PropTypes.number,
    designations: PropTypes.string,
    email: PropTypes.string,
    isIndianaLicensed: PropTypes.bool,
    isKentuckyLicensed: PropTypes.bool,
    isOhioLicensed: PropTypes.bool,
    languages: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    photoUrl: PropTypes.string,
    professionalTitle: PropTypes.string,
    teamName: PropTypes.string,
    websiteUrl: PropTypes.string,
    __typename: PropTypes.string
  })
};

export default BlogDetail;
